import React, { useEffect, useMemo, useState, useRef } from "react";
import toast from "react-hot-toast";
import { Sparkles, ChevronLeft, ChevronRight, FileDown, Save, Loader2, Wand2, Plus, X } from "lucide-react";
import { api, fmtErr, BACKEND } from "../lib/api";
import { useAuth } from "../lib/auth";
import { POSITIONING_STEPS } from "../lib/positioning-schema";
import RodaPosicionamento from "../components/RodaPosicionamento";
import PerceptualMap from "../components/PerceptualMap";

function ListInput({ value, onChange, placeholder }) {
  const [tmp, setTmp] = useState("");
  const items = Array.isArray(value) ? value : [];
  const add = () => { if (tmp.trim()) { onChange([...items, tmp.trim()]); setTmp(""); } };
  return (
    <div>
      <div className="flex gap-2">
        <input className="input" placeholder={placeholder} value={tmp} onChange={(e) => setTmp(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} />
        <button type="button" onClick={add} className="btn-outline"><Plus size={16} /></button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {items.map((it, i) => (
          <span key={`${it}-${i}`} className="chip flex items-center gap-1">
            {it}
            <button onClick={() => onChange(items.filter((_, x) => x !== i))} className="text-muted hover:text-[var(--error)]"><X size={11} /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

function Field({ f, value, onChange }) {
  if (f.type === "textarea") return <textarea className="input" rows={4} placeholder={f.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${f.key}`} />;
  if (f.type === "select") return (
    <select className="input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${f.key}`}>
      <option value="">Selecione…</option>
      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  if (f.type === "number") return <input type="number" className="input" min={f.min} max={f.max} value={value ?? ""} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : "")} data-testid={`field-${f.key}`} />;
  if (f.type === "list") return <ListInput value={value} onChange={onChange} placeholder={f.placeholder || "Digite e Enter"} />;
  if (f.type === "perceptual_map") return <PerceptualMap value={value || {}} onChange={onChange} />;
  return <input className="input" placeholder={f.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${f.key}`} />;
}

function ProgressWheel({ progress }) {
  const percent = progress?.percent || 0;
  const cx = 100, cy = 100, r = 80;
  return (
    <svg viewBox="0 0 200 200" className="w-full max-w-[220px]">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border)" strokeWidth="10" />
      <circle
        cx={cx} cy={cy} r={r} fill="none" stroke="url(#g)" strokeWidth="10"
        strokeDasharray={`${(percent / 100) * (2 * Math.PI * r)} ${2 * Math.PI * r}`}
        strokeLinecap="round" transform={`rotate(-90 ${cx} ${cy})`}
      />
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FF7A5A" />
          <stop offset="0.55" stopColor="#FF4E9E" />
          <stop offset="1" stopColor="#C05BFF" />
        </linearGradient>
      </defs>
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize="30" fontWeight="800" fill="currentColor">{percent}%</text>
      <text x={cx} y={cy + 20} textAnchor="middle" fontSize="10" fill="var(--muted)" style={{ letterSpacing: 2 }}>PROGRESSO</text>
    </svg>
  );
}

export default function Positioning() {
  const { activeBrand } = useAuth();
  const [pos, setPos] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [values, setValues] = useState({});
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const step = POSITIONING_STEPS[currentIdx];

  useEffect(() => {
    if (!activeBrand) return;
    (async () => {
      const { data } = await api.get(`/positioning/${activeBrand.brand_id}`);
      setPos(data);
      const stepData = data.steps?.[POSITIONING_STEPS[currentIdx].key]?.data || {};
      setValues(stepData);
    })();
    // eslint-disable-next-line
  }, [activeBrand]);

  useEffect(() => {
    if (!pos) return;
    // Reset values only when the STEP CHANGES (not on every pos refresh).
    // This avoids clobbering in-flight typed characters when autosave refreshes pos.
    const stepData = pos.steps?.[step.key]?.data || {};
    setValues(stepData);
    setAiText("");
    // eslint-disable-next-line
  }, [currentIdx]);

  const autosave = (newVals) => {
    if (!activeBrand) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await api.put(`/positioning/${activeBrand.brand_id}/step`, { step_key: step.key, data: newVals });
        // refresh progress
        const { data } = await api.get(`/positioning/${activeBrand.brand_id}`);
        setPos(data);
      } catch (err) {
        toast.error(fmtErr(err.response?.data?.detail));
      } finally { setSaving(false); }
    }, 700);
  };

  const setField = (key, val) => {
    const nv = { ...values, [key]: val };
    setValues(nv);
    autosave(nv);
  };

  const askAI = async () => {
    if (!step.aiPrompt) return;
    setAiLoading(true);
    setAiText("");
    try {
      const prompt = step.aiPrompt(values, activeBrand, pos?.steps || {});
      const { data } = await api.post(`/ai/assist`, { prompt, max_tokens: 900 });
      setAiText(data.text);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally { setAiLoading(false); }
  };

  const exportPdf = async () => {
    if (!activeBrand) return;
    try {
      const token = localStorage.getItem("sb_token");
      const r = await fetch(`${BACKEND}/api/positioning/${activeBrand.brand_id}/export/pdf`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error("PDF");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `posicionamento-${activeBrand.name.replace(/\s+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Falha ao gerar PDF"); }
  };

  if (!pos) return <div className="p-10 text-muted font-mono text-xs">carregando…</div>;

  return (
    <div className="p-6 lg:p-10" data-testid="positioning-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-1">// módulo 2 · coração</div>
          <h1 className="text-3xl font-extrabold">Posicionamento</h1>
          <p className="text-muted text-sm mt-1">Wizard de 14 etapas com auto-save. Assistentes de IA por etapa.</p>
        </div>
        <div className="flex items-center gap-3">
          {saving && <span className="text-xs text-muted flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> salvando…</span>}
          <button onClick={exportPdf} className="btn-outline text-sm flex items-center gap-2" data-testid="export-pdf">
            <FileDown size={15} /> Exportar PDF
          </button>
        </div>
      </div>

      {/* Roda Você / Mercado / Sociedade — reflete o preenchimento por grupo */}
      <div className="card p-5 mb-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 50%, rgba(192,91,255,0.4), transparent 60%)" }} />
        <div className="relative grid md:grid-cols-[220px_1fr] gap-6 items-center">
          <div className="flex justify-center">
            <div style={{ width: 220 }}>
              <RodaPosicionamento steps={pos.steps || {}} size={220} />
            </div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-1">// roda de posicionamento</div>
            <h3 className="text-lg font-bold">Você × Mercado × Sociedade convergindo</h3>
            <p className="text-sm text-muted mt-1 max-w-lg">O centro "Posicionamento" acende conforme as três frentes se preenchem. Cada etapa contribui pra um dos vetores.</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        {/* Left: steps list + wheel */}
        <aside className="space-y-4">
          <div className="card p-5 flex items-center justify-center">
            <ProgressWheel progress={pos.progress} />
          </div>
          <div className="card p-3 space-y-1 max-h-[500px] overflow-y-auto" data-testid="steps-nav">
            {POSITIONING_STEPS.map((s, i) => {
              const done = !!pos.steps?.[s.key]?.data && Object.keys(pos.steps[s.key].data).some((k) => {
                const v = pos.steps[s.key].data[k];
                return v && (Array.isArray(v) ? v.length > 0 : true);
              });
              const active = i === currentIdx;
              return (
                <button key={s.key} onClick={() => setCurrentIdx(i)} className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${active ? "grad-bg text-white" : "hover:bg-[var(--hover)]"}`} data-testid={`step-nav-${s.key}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${active ? "bg-white/20" : done ? "bg-[var(--success)] text-black" : "card-2"}`}>
                    {done ? "✓" : i + 1}
                  </span>
                  <span className={`truncate ${active ? "" : "text-[var(--txt)]"}`}>{s.title}</span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Right: form */}
        <section className="space-y-4">
          <div className="card p-6">
            <div className="flex items-baseline justify-between mb-1">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted">etapa {currentIdx + 1} de {POSITIONING_STEPS.length}</div>
              <div className="text-xs text-muted">{pos.progress.percent}%</div>
            </div>
            <h2 className="text-2xl font-bold" data-testid="step-title">{step.title}</h2>
            <p className="text-muted text-sm mt-1">{step.desc}</p>
            <div className="progress mt-3"><div style={{ width: `${((currentIdx + 1) / POSITIONING_STEPS.length) * 100}%` }} /></div>
          </div>

          <div className="card p-6 space-y-4">
            {step.fields.map((f) => (
              <div key={f.key}>
                <label className="text-xs text-muted font-mono uppercase tracking-widest">{f.label}</label>
                <div className="mt-1.5">
                  <Field f={f} value={values[f.key]} onChange={(v) => setField(f.key, v)} />
                </div>
              </div>
            ))}
          </div>

          {step.aiPrompt && (
            <div className="card p-6 space-y-3" data-testid="ai-panel">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="grad-text" />
                  <span className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// assistente de ia</span>
                </div>
                <button onClick={askAI} disabled={aiLoading} className="btn-grad text-sm flex items-center gap-2" data-testid="ai-ask">
                  {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                  {aiLoading ? "pensando…" : "sugerir"}
                </button>
              </div>
              {aiText && <div className="text-sm leading-relaxed whitespace-pre-wrap card-2 p-4">{aiText}</div>}
              {!aiText && <p className="text-xs text-muted">A IA analisa suas respostas nesta etapa (e nas anteriores) e sugere insights. Configure sua chave em Configurações.</p>}
            </div>
          )}

          {step.key === "documento_final" && (
            <div className="card p-6 space-y-3 relative overflow-hidden">
              <div className="absolute inset-0 opacity-15 grad-bg pointer-events-none" />
              <div className="relative">
                <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// entrega final</div>
                <h3 className="text-xl font-bold mt-1">Baixe seu documento estratégico</h3>
                <p className="text-sm text-muted mt-1">Todas as 14 etapas em PDF pronto para apresentar.</p>
                <button onClick={exportPdf} className="btn-grad mt-4 flex items-center gap-2 text-sm"><FileDown size={15} /> Baixar PDF</button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <button onClick={() => setCurrentIdx(Math.max(0, currentIdx - 1))} disabled={currentIdx === 0} className="btn-outline flex items-center gap-1 text-sm disabled:opacity-40" data-testid="prev-step">
              <ChevronLeft size={16} /> Anterior
            </button>
            <button onClick={() => setCurrentIdx(Math.min(POSITIONING_STEPS.length - 1, currentIdx + 1))} disabled={currentIdx === POSITIONING_STEPS.length - 1} className="btn-grad flex items-center gap-1 text-sm" data-testid="next-step">
              Próxima <ChevronRight size={16} />
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
